// src/pages/DuesPage.js
import { useState, useEffect } from 'react';

import { useAuth } from '../context/AuthContext';
import { getDuesForMember, getHouseAggregateDues } from '../api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import NavBar from '../components/shared/NavBar';

const NAVY = '#1B3A6B';
const GOLD = '#C9A84C';
const GREEN = '#1a6b3a';
const RED = '#c0392b';

export default function DuesPage() {
  const { member, role, boardingHouse } = useAuth();

  const [dues,          setDues]          = useState([]);
  const [houseAggregate, setHouseAggregate] = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [activeTab,     setActiveTab]     = useState('personal');

  const canSeeHouse = role === 'executive' ||
                      role === 'house_rep' ||
                      role === 'member';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [duesResult, houseResult] = await Promise.all([
          getDuesForMember(member?.id),
          canSeeHouse
            ? getHouseAggregateDues(boardingHouse)
            : Promise.resolve(null)
        ]);
        setDues(duesResult.dues || []);
        if (houseResult) setHouseAggregate(houseResult);
      } catch (err) {
        setError('Could not load dues: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    if (member?.id) load();
  }, [member, boardingHouse, canSeeHouse]); // eslint-disable-line react-hooks/exhaustive-deps


  // Totals
  const totalOwed    = dues.reduce((s, d) => s + (Number(d.amountOwed) || 0), 0);
  const totalPaid    = dues.reduce((s, d) => s + (Number(d.amountPaid) || 0), 0);
  const totalBalance = totalOwed - totalPaid;

  // Chart data
  const chartData = dues.map(d => ({
    year:    d.year?.toString(),
    Owed:    Number(d.amountOwed) || 0,
    Paid:    Number(d.amountPaid) || 0,
    Balance: (Number(d.amountOwed) || 0) - (Number(d.amountPaid) || 0)
  }));

  return (
    <div style={{
      minHeight: '100vh', background: '#f0f4f8',
      fontFamily: 'Arial, sans-serif'
    }}>

      {/* Nav */}
      <NavBar />

      <div style={{
        maxWidth: '900px', margin: '0 auto',
        padding: '24px 16px'
      }}>

        {/* Page Header */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
          borderRadius: '12px', padding: '24px',
          marginBottom: '20px', color: '#fff'
        }}>
          <h2 style={{ margin: '0 0 4px', fontSize: '22px' }}>
            💰 Membership Dues
          </h2>
          <p style={{ margin: 0, opacity: 0.85, fontSize: '14px' }}>
            {member?.name} — {boardingHouse} House
          </p>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: RED, fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: '8px', marginBottom: '20px'
        }}>
          {[
            { id: 'personal', label: '👤 My Dues' },
            { id: 'house',    label: `🏠 ${boardingHouse} House` }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 20px',
                background: activeTab === tab.id ? NAVY : '#fff',
                color: activeTab === tab.id ? '#fff' : NAVY,
                border: `2px solid ${NAVY}`,
                borderRadius: '8px', cursor: 'pointer',
                fontSize: '14px', fontWeight: '600'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{
            textAlign: 'center', padding: '60px',
            color: '#666', fontSize: '14px'
          }}>
            Loading dues...
          </div>
        ) : (
          <>
            {/* ── PERSONAL DUES TAB ── */}
            {activeTab === 'personal' && (
              <div>
                {/* Summary Cards */}
                <div className="dues-cards-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '16px', marginBottom: '20px'
                }}>
                  {[
                    {
                      label: 'Total Owed',
                      value: totalOwed,
                      color: '#333',
                      bg: '#fff'
                    },
                    {
                      label: 'Total Paid',
                      value: totalPaid,
                      color: GREEN,
                      bg: '#d4edda'
                    },
                    {
                      label: 'Outstanding',
                      value: totalBalance,
                      color: totalBalance > 0 ? RED : GREEN,
                      bg: totalBalance > 0 ? '#fdecea' : '#d4edda'
                    }
                  ].map(card => (
                    <div key={card.label} style={{
                      background: card.bg,
                      borderRadius: '12px', padding: '20px',
                      textAlign: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}>
                      <div style={{
                        fontSize: '24px', fontWeight: 'bold',
                        color: card.color, marginBottom: '4px'
                      }}>
                        {card.value.toLocaleString()}
                      </div>
                      <div style={{
                        fontSize: '12px', color: '#888',
                        fontWeight: '600', textTransform: 'uppercase'
                      }}>
                        {card.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Chart */}
                {chartData.length > 0 && (
                  <div style={{
                    background: '#fff', borderRadius: '12px',
                    padding: '20px', marginBottom: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}>
                    <h3 style={{
                      color: NAVY, margin: '0 0 16px',
                      fontSize: '16px'
                    }}>
                      Dues by Year
                    </h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="Owed"    fill={NAVY} />
                        <Bar dataKey="Paid"    fill={GOLD} />
                        <Bar dataKey="Balance" fill={RED}  />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Dues Table */}
                <div style={{
                  background: '#fff', borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <h3 style={{
                      color: NAVY, margin: 0, fontSize: '16px'
                    }}>
                      Dues History
                    </h3>
                  </div>

                  {dues.length === 0 ? (
                    <div style={{
                      padding: '40px', textAlign: 'center',
                      color: '#999', fontSize: '14px'
                    }}>
                      No dues records found.
                      <br />
                      <span style={{ fontSize: '12px' }}>
                        Contact your house representative or an executive.
                      </span>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%', borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa' }}>
                          {['Year', 'Amount Owed', 'Amount Paid',
                            'Balance', 'Status'].map(h => (
                            <th key={h} style={{
                              padding: '12px 16px', textAlign: 'left',
                              fontSize: '12px', color: '#888',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              borderBottom: '1px solid #f0f0f0'
                            }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dues.map((due, i) => {
                          const owed    = Number(due.amountOwed) || 0;
                          const paid    = Number(due.amountPaid) || 0;
                          const balance = owed - paid;
                          const paid100 = balance <= 0;
                          return (
                            <tr key={due.id || i} style={{
                              borderBottom: '1px solid #f0f0f0'
                            }}>
                              <td style={{
                                padding: '14px 16px',
                                fontWeight: 'bold', color: NAVY
                              }}>
                                {due.year}
                              </td>
                              <td style={{
                                padding: '14px 16px', color: '#333'
                              }}>
                                {owed.toLocaleString()}
                              </td>
                              <td style={{
                                padding: '14px 16px', color: GREEN,
                                fontWeight: '600'
                              }}>
                                {paid.toLocaleString()}
                              </td>
                              <td style={{
                                padding: '14px 16px',
                                color: paid100 ? GREEN : RED,
                                fontWeight: '600'
                              }}>
                                {balance.toLocaleString()}
                              </td>
                              <td style={{ padding: '14px 16px' }}>
                                <span style={{
                                  background: paid100
                                    ? '#d4edda' : '#fdecea',
                                  color: paid100 ? GREEN : RED,
                                  padding: '3px 10px',
                                  borderRadius: '10px',
                                  fontSize: '12px', fontWeight: 'bold'
                                }}>
                                  {paid100 ? '✓ Paid' : 'Outstanding'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── HOUSE AGGREGATE TAB ── */}
            {activeTab === 'house' && (
              <div>
                {!houseAggregate ? (
                  <div style={{
                    background: '#fff', borderRadius: '12px',
                    padding: '40px', textAlign: 'center',
                    color: '#999', fontSize: '14px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}>
                    No house dues data available.
                  </div>
                ) : (
                  <>
                    {/* House Summary Cards */}
                    <div className="house-dues-cards-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '16px', marginBottom: '20px'
                    }}>
                      {[
                        {
                          label: 'Total Members',
                          value: houseAggregate.totalMembers,
                          color: NAVY, bg: '#fff',
                          suffix: 'members'
                        },
                        {
                          label: 'Collection Rate',
                          value: houseAggregate.totalOwed > 0
                            ? Math.round((houseAggregate.totalPaid /
                                houseAggregate.totalOwed) * 100)
                            : 0,
                          color: GOLD, bg: '#fff',
                          suffix: '%'
                        },
                        {
                          label: 'Total Collected',
                          value: (houseAggregate.totalPaid || 0)
                            .toLocaleString(),
                          color: GREEN, bg: '#d4edda',
                          suffix: ''
                        },
                        {
                          label: 'Total Outstanding',
                          value: (houseAggregate.totalBalance || 0)
                            .toLocaleString(),
                          color: RED, bg: '#fdecea',
                          suffix: ''
                        }
                      ].map(card => (
                        <div key={card.label} style={{
                          background: card.bg,
                          borderRadius: '12px', padding: '20px',
                          textAlign: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}>
                          <div style={{
                            fontSize: '28px', fontWeight: 'bold',
                            color: card.color, marginBottom: '4px'
                          }}>
                            {card.value}{card.suffix}
                          </div>
                          <div style={{
                            fontSize: '12px', color: '#888',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            {card.label}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* House by Year breakdown */}
                    {houseAggregate.byYear && (
                      <div style={{
                        background: '#fff', borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          padding: '16px 20px',
                          borderBottom: '1px solid #f0f0f0'
                        }}>
                          <h3 style={{
                            color: NAVY, margin: 0, fontSize: '16px'
                          }}>
                            {boardingHouse} House — By Year
                          </h3>
                          <p style={{
                            color: '#888', margin: '4px 0 0',
                            fontSize: '12px'
                          }}>
                            Aggregate totals only — individual
                            contributions are private
                          </p>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                        <table style={{
                          width: '100%', borderCollapse: 'collapse'
                        }}>
                          <thead>
                            <tr style={{ background: '#f8f9fa' }}>
                              {['Year', 'Total Owed', 'Total Paid',
                                'Outstanding', 'Rate'].map(h => (
                                <th key={h} style={{
                                  padding: '12px 16px',
                                  textAlign: 'left',
                                  fontSize: '12px', color: '#888',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  borderBottom: '1px solid #f0f0f0'
                                }}>
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(houseAggregate.byYear)
                              .sort((a, b) => b[0] - a[0])
                              .map(([year, data], i) => {
                                const rate = data.owed > 0
                                  ? Math.round(
                                      (data.paid / data.owed) * 100
                                    )
                                  : 0;
                                return (
                                  <tr key={year} style={{
                                    borderBottom: '1px solid #f0f0f0'
                                  }}>
                                    <td style={{
                                      padding: '14px 16px',
                                      fontWeight: 'bold', color: NAVY
                                    }}>
                                      {year}
                                    </td>
                                    <td style={{
                                      padding: '14px 16px',
                                      color: '#333'
                                    }}>
                                      {data.owed.toLocaleString()}
                                    </td>
                                    <td style={{
                                      padding: '14px 16px',
                                      color: GREEN, fontWeight: '600'
                                    }}>
                                      {data.paid.toLocaleString()}
                                    </td>
                                    <td style={{
                                      padding: '14px 16px',
                                      color: data.owed - data.paid > 0
                                        ? RED : GREEN,
                                      fontWeight: '600'
                                    }}>
                                      {(data.owed - data.paid)
                                        .toLocaleString()}
                                    </td>
                                    <td style={{
                                      padding: '14px 16px'
                                    }}>
                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'center', gap: '8px'
                                      }}>
                                        <div style={{
                                          flex: 1, height: '8px',
                                          background: '#f0f0f0',
                                          borderRadius: '4px',
                                          overflow: 'hidden'
                                        }}>
                                          <div style={{
                                            width: `${rate}%`,
                                            height: '100%',
                                            background: rate >= 75
                                              ? GREEN
                                              : rate >= 50
                                              ? GOLD : RED,
                                            borderRadius: '4px'
                                          }} />
                                        </div>
                                        <span style={{
                                          fontSize: '12px',
                                          fontWeight: 'bold',
                                          color: rate >= 75
                                            ? GREEN
                                            : rate >= 50
                                            ? '#856404' : RED,
                                          minWidth: '36px'
                                        }}>
                                          {rate}%
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}